import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Droplet, 
  Globe, 
  Users, 
  Calendar, 
  MapPin, 
  Heart,
  ArrowRight, 
  Camera, 
  AlertTriangle,
  Sprout,
  Zap,
  CloudRain,
  Check,
  Shield,
  FileText,
  PanelRight
} from 'lucide-react';
import { motion } from 'framer-motion';
import AnimatedSection from '../components/AnimatedSection';
import SectionHeader from '../components/shared/SectionHeader';
import { Card, Badge, Button } from '../components/ui';

// Water source types with colors
const waterSourceTypes = [
  { 
    status: 'abundant', 
    label: 'Abundant', 
    description: 'Plentiful water flow, full capacity', 
    color: 'bg-blue-500' 
  },
  { 
    status: 'normal', 
    label: 'Normal', 
    description: 'Regular water flow, stable conditions', 
    color: 'bg-green-500' 
  },
  { 
    status: 'reduced', 
    label: 'Reduced', 
    description: 'Below normal flow, potential concern', 
    color: 'bg-amber-500' 
  },
  { 
    status: 'critical', 
    label: 'Critical', 
    description: 'Severely diminished, urgent attention needed', 
    color: 'bg-red-500' 
  },
  { 
    status: 'restored', 
    label: 'Restored', 
    description: 'Revitalized from previous degradation', 
    color: 'bg-purple-500' 
  },
  { 
    status: 'sacred', 
    label: 'Sacred', 
    description: 'Water site with cultural or spiritual significance', 
    color: 'bg-teal-500' 
  }
];

// Sample featured elders
const featuredElders = [
  {
    name: 'Maria Waters',
    tribe: 'Lakota',
    role: 'Elder',
    location: 'South Dakota, USA',
    image: 'https://images.unsplash.com/photo-1542909168-82c3e7fdca5c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=300&h=300&q=80',
    story: 'Guardian of the sacred waters for three decades, working with communities to protect watersheds.'
  },
  {
    name: 'Anahita Flowing',
    tribe: 'Persian',
    role: 'Elder',
    location: 'Tehran, Iran',
    image: 'https://images.unsplash.com/photo-1545133875-9c6a76451ae6?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=300&h=300&q=80',
    story: 'Carries ancient water wisdom from Persian traditions, focusing on water conservation.'
  }
];

// Sample upcoming events
const upcomingEvents = [
  {
    title: 'Global Water Blessing Ceremony',
    date: new Date(new Date().setDate(new Date().getDate() + 14)),
    location: 'Online',
    type: 'ceremony',
    isOnline: true,
    isFullMoon: true
  },
  {
    title: 'River Clean-up Day',
    date: new Date(new Date().setDate(new Date().getDate() + 7)),
    location: 'Blue River Park, New York',
    type: 'cleanup',
    isOnline: false,
    isFullMoon: false
  },
  {
    title: 'Water Wisdom Circle',
    date: new Date(new Date().setDate(new Date().getDate() + 3)),
    location: 'Community Center, Los Angeles',
    type: 'gathering',
    isOnline: false,
    isFullMoon: false
  }
];

// Sample featured resources
const featuredResources = [
  {
    title: 'Water is Life: Elder Teachings',
    type: 'video',
    categories: ['indigenous knowledge', 'water ceremonies', 'traditional practices'],
    featured: true,
    elderTeaching: true
  },
  {
    title: 'Water Blessing Protocol',
    type: 'text',
    categories: ['ceremony', 'ritual', 'practice guide'],
    featured: true,
    elderTeaching: false
  }
];

const WaterUnityPage: React.FC = () => {
  const [activeTypeIndex, setActiveTypeIndex] = useState(0);
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Hero Section */}
      <div className="relative h-screen">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1520034475321-cbe63696469a?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1920&q=80"
            alt="Sacred waters"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-blue-900/70 via-blue-800/60 to-blue-900/80" />
        </div>
        
        <div className="absolute inset-0 flex items-center">
          <div className="container mx-auto px-4">
            <AnimatedSection>
              <div className="max-w-3xl">
                <Badge 
                  variant="blue" 
                  className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 text-white mb-8"
                  icon={<Droplet className="h-5 w-5" />}
                >
                  Water Unity Network
                </Badge>
                
                <h1 className="text-6xl font-bold text-white mb-6 leading-tight">
                  Preserving Sacred Waters Through Indigenous Wisdom
                </h1>
                
                <p className="text-xl text-white/90 mb-10">
                  A global network of water protectors, indigenous elders, and allies working to safeguard sacred waters and share traditional water wisdom.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    variant="primary" 
                    size="lg" 
                    className="bg-blue-600 hover:bg-blue-700 border-none"
                  >
                    <Link to="/water-unity/join" className="flex items-center gap-2 text-white">
                      <Users className="h-5 w-5" />
                      Join Our Network
                    </Link>
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="border-white text-white hover:bg-white/10"
                  >
                    <Link to="/water-unity/dashboard" className="flex items-center gap-2">
                      <Droplet className="h-5 w-5" />
                      Water Unity Dashboard
                    </Link>
                  </Button>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
        
        {/* Animated Wave Overlay */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            width="100%" 
            height="40" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
            viewBox="0 0 1200 120"
            className="text-blue-50"
          >
            <path
              d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
              opacity=".5"
              fill="currentColor"
            />
            <path
              d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
              opacity=".5"
              fill="currentColor"
            />
            <path
              d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
              fill="currentColor"
            />
          </svg>
        </div>
      </div>

      {/* Our Mission */}
      <div className="py-24 bg-blue-50">
        <div className="container mx-auto px-4">
          <SectionHeader
            title="Our Mission"
            subtitle="Revitalizing our relationship with water through ancestral wisdom and community action"
            icon={<Droplet className="h-5 w-5" />}
            badgeText="About Water Unity"
            badgeColor="blue"
          />
          
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <AnimatedSection delay={0.1}>
              <div className="space-y-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  Water Unity is a global network of water protectors, elders, youth, and allies working to safeguard sacred waters and share traditional water wisdom for future generations.
                </p>
                
                <p className="text-lg text-gray-700 leading-relaxed">
                  We are guided by the understanding that water is sacred and alive—a being with consciousness that deserves our respect and protection. Our approach combines indigenous knowledge with community action.
                </p>
                
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                    <Heart className="h-5 w-5 text-blue-600" />
                    <span>Our Core Values</span>
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">Honoring Water as Sacred</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">Indigenous Leadership</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">Community Empowerment</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">Knowledge Sharing</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">Intergenerational Wisdom</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">Reciprocal Relationship</span>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.3}>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1505513499487-d6128e3753e6?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80" 
                  alt="Elder by sacred waters" 
                  className="rounded-xl shadow-lg w-full h-auto"
                />
                
                <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-xl shadow-lg max-w-xs">
                  <p className="text-gray-800 italic text-sm">
                    "Water is not a resource; it is the source of life itself. When we protect water, we protect everything."
                  </p>
                  <p className="text-right text-gray-600 text-xs mt-2">
                    — Indigenous Water Teaching
                  </p>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>
      
      {/* Water Source Types */}
      <div className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <SectionHeader
            title="Understanding Water Sources"
            subtitle="Learn about different types of water sources and their significance in our network"
            icon={<Droplet className="h-5 w-5" />}
            badgeText="Water Classification"
            badgeColor="blue"
          />
          
          <div className="max-w-6xl mx-auto">
            {/* Water Type Selector */}
            <div className="flex overflow-x-auto py-4 mb-8 no-scrollbar">
              <div className="flex gap-4 mx-auto">
                {waterSourceTypes.map((type, index) => (
                  <button
                    key={type.status}
                    className={`flex flex-col items-center p-3 rounded-lg transition-colors ${
                      activeTypeIndex === index 
                        ? 'bg-blue-50 border border-blue-200' 
                        : 'hover:bg-gray-100'
                    }`}
                    onClick={() => setActiveTypeIndex(index)}
                  >
                    <div className={`w-4 h-4 rounded-full ${type.color} mb-2`}></div>
                    <span className="text-sm font-medium whitespace-nowrap">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Active Water Type Description */}
            <AnimatedSection>
              <div className="bg-blue-50 rounded-xl p-8 shadow-lg">
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-8 h-8 rounded-full ${waterSourceTypes[activeTypeIndex].color}`}></div>
                  <h3 className="text-2xl font-bold">{waterSourceTypes[activeTypeIndex].label} Waters</h3>
                </div>
                
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                      {waterSourceTypes[activeTypeIndex].status === 'abundant' && (
                        "Abundant water sources are characterized by plentiful flow and capacity, often representing healthy, thriving water systems. These waters support diverse ecosystems and provide reliable water access to surrounding communities."
                      )}
                      {waterSourceTypes[activeTypeIndex].status === 'normal' && (
                        "Normal water sources maintain stable conditions with regular flow patterns. These represent balanced water systems that are functioning as expected, supporting local ecosystems and community needs without excess or shortage."
                      )}
                      {waterSourceTypes[activeTypeIndex].status === 'reduced' && (
                        "Reduced water sources show below normal flow levels, indicating potential concerns about water availability. These conditions may be due to drought, overuse, or changes in watershed conditions, requiring monitoring and conservation efforts."
                      )}
                      {waterSourceTypes[activeTypeIndex].status === 'critical' && (
                        "Critical water sources are severely diminished, facing serious threats to their existence and function. These waters require urgent attention and intervention to prevent complete loss and to restore flow and health to sustainable levels."
                      )}
                      {waterSourceTypes[activeTypeIndex].status === 'restored' && (
                        "Restored water sources have been revitalized from previous degradation through active conservation, cleanup efforts, or policy changes. These represent success stories of water healing and renewal, often through community-led initiatives."
                      )}
                      {waterSourceTypes[activeTypeIndex].status === 'sacred' && (
                        "Sacred water sources hold special cultural or spiritual significance to indigenous communities and traditional peoples. These waters are central to ceremonies, creation stories, and indigenous identity, requiring special protection and respect."
                      )}
                    </p>
                    
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center gap-3">
                        {waterSourceTypes[activeTypeIndex].status === 'abundant' && (
                          <Zap className="h-6 w-6 text-blue-600" />
                        )}
                        {waterSourceTypes[activeTypeIndex].status === 'normal' && (
                          <Check className="h-6 w-6 text-green-600" />
                        )}
                        {waterSourceTypes[activeTypeIndex].status === 'reduced' && (
                          <AlertTriangle className="h-6 w-6 text-amber-600" />
                        )}
                        {waterSourceTypes[activeTypeIndex].status === 'critical' && (
                          <AlertTriangle className="h-6 w-6 text-red-600" />
                        )}
                        {waterSourceTypes[activeTypeIndex].status === 'restored' && (
                          <Sprout className="h-6 w-6 text-purple-600" />
                        )}
                        {waterSourceTypes[activeTypeIndex].status === 'sacred' && (
                          <Heart className="h-6 w-6 text-teal-600" />
                        )}
                        <span className="font-bold">How to Help</span>
                      </div>
                      
                      <ul className="list-disc pl-10 space-y-2 text-gray-700">
                        {waterSourceTypes[activeTypeIndex].status === 'abundant' && (
                          <>
                            <li>Monitor and document healthy conditions</li>
                            <li>Study ecosystem balance and biodiversity</li>
                            <li>Protect surrounding watershed from development</li>
                            <li>Establish conservation agreements</li>
                          </>
                        )}
                        {waterSourceTypes[activeTypeIndex].status === 'normal' && (
                          <>
                            <li>Maintain regular monitoring schedules</li>
                            <li>Implement sustainable usage policies</li>
                            <li>Educate communities on water conservation</li>
                            <li>Develop early warning systems for changes</li>
                          </>
                        )}
                        {waterSourceTypes[activeTypeIndex].status === 'reduced' && (
                          <>
                            <li>Increase monitoring frequency</li>
                            <li>Implement water conservation measures</li>
                            <li>Investigate causes of reduction</li>
                            <li>Develop restoration plans</li>
                            <li>Engage community in conservation efforts</li>
                          </>
                        )}
                        {waterSourceTypes[activeTypeIndex].status === 'critical' && (
                          <>
                            <li>Implement emergency conservation measures</li>
                            <li>Conduct comprehensive assessment of threats</li>
                            <li>Develop immediate intervention strategies</li>
                            <li>Engage government and conservation agencies</li>
                            <li>Mobilize community and media attention</li>
                          </>
                        )}
                        {waterSourceTypes[activeTypeIndex].status === 'restored' && (
                          <>
                            <li>Document restoration process and outcomes</li>
                            <li>Monitor for continued improvement</li>
                            <li>Share success stories and methodologies</li>
                            <li>Implement protective measures to prevent regression</li>
                            <li>Engage community in celebration and maintenance</li>
                          </>
                        )}
                        {waterSourceTypes[activeTypeIndex].status === 'sacred' && (
                          <>
                            <li>Support indigenous-led protection efforts</li>
                            <li>Respect cultural protocols and access restrictions</li>
                            <li>Document traditions with permission from elders</li>
                            <li>Advocate for legal protections based on cultural significance</li>
                            <li>Support cultural revitalization efforts</li>
                          </>
                        )}
                      </ul>
                    </div>
                  </div>
                  
                  <div className="relative">
                    {waterSourceTypes[activeTypeIndex].status === 'abundant' && (
                      <img 
                        src="https://images.unsplash.com/photo-1566438480900-0609be27a4be?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80" 
                        alt="Abundant water flow" 
                        className="w-full h-full object-cover rounded-lg"
                      />
                    )}
                    {waterSourceTypes[activeTypeIndex].status === 'normal' && (
                      <img 
                        src="https://images.unsplash.com/photo-1515641555578-9c7b5a5a1ccb?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80" 
                        alt="Normal water flow" 
                        className="w-full h-full object-cover rounded-lg"
                      />
                    )}
                    {waterSourceTypes[activeTypeIndex].status === 'reduced' && (
                      <img 
                        src="https://images.unsplash.com/photo-1536240249696-8d9ecf0e4c01?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80" 
                        alt="Reduced water flow" 
                        className="w-full h-full object-cover rounded-lg"
                      />
                    )}
                    {waterSourceTypes[activeTypeIndex].status === 'critical' && (
                      <img 
                        src="https://images.unsplash.com/photo-1566045926227-053e2d35a25d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80" 
                        alt="Critical water shortage" 
                        className="w-full h-full object-cover rounded-lg"
                      />
                    )}
                    {waterSourceTypes[activeTypeIndex].status === 'restored' && (
                      <img 
                        src="https://images.unsplash.com/photo-1618477461853-cf6ed80faba5?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80" 
                        alt="Restored water source" 
                        className="w-full h-full object-cover rounded-lg"
                      />
                    )}
                    {waterSourceTypes[activeTypeIndex].status === 'sacred' && (
                      <img 
                        src="https://images.unsplash.com/photo-1519815099892-a6e1781fc10f?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80" 
                        alt="Sacred water source" 
                        className="w-full h-full object-cover rounded-lg"
                      />
                    )}
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>
      
      {/* Featured Elders */}
      <div className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <SectionHeader
            title="Featured Water Keepers"
            subtitle="Meet the elders and guardians who lead our water unity movement"
            icon={<Users className="h-5 w-5" />}
            badgeText="Wisdom Keepers"
            badgeColor="blue"
          />
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {featuredElders.map((elder, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <Card className="flex flex-col md:flex-row p-0 overflow-hidden">
                  <div className="w-full md:w-1/3">
                    <img 
                      src={elder.image} 
                      alt={elder.name} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6 flex-1">
                    <h3 className="text-xl font-bold mb-1">{elder.name}</h3>
                    <div className="text-gray-600 mb-3 flex flex-wrap gap-2 items-center">
                      <span>{elder.role}</span>
                      <span className="text-gray-400">•</span>
                      <span>{elder.tribe}</span>
                      <span className="text-gray-400">•</span>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        <span>{elder.location}</span>
                      </div>
                    </div>
                    <p className="text-gray-700 mb-4">
                      {elder.story}
                    </p>
                    <Link 
                      to="/water-unity/profiles"
                      className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm font-medium"
                    >
                      <span>View Full Profile</span>
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </Card>
              </AnimatedSection>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <Link 
              to="/water-unity/profiles" 
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
            >
              <span>View All Water Keepers</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>
      
      {/* Upcoming Events */}
      <div className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <SectionHeader
            title="Upcoming Water Ceremonies & Events"
            subtitle="Join our community gatherings, ceremonies, and water protection events"
            icon={<Calendar className="h-5 w-5" />}
            badgeText="Gatherings"
            badgeColor="blue"
          />
          
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {upcomingEvents.map((event, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <Card className="h-full flex flex-col">
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <Badge 
                        variant={
                          event.type === 'ceremony' ? 'purple' : 
                          event.type === 'cleanup' ? 'green' : 
                          'blue'
                        } 
                        className="capitalize"
                      >
                        {event.type}
                      </Badge>
                      <div className="flex gap-2">
                        {event.isOnline && (
                          <Badge variant="gray" icon={<Globe className="h-3 w-3" />}>
                            Online
                          </Badge>
                        )}
                        {event.isFullMoon && (
                          <Badge variant="amber" icon={<Droplet className="h-3 w-3" />}>
                            Full Moon
                          </Badge>
                        )}
                      </div>
                    </div>
                    <h3 className="font-bold text-xl mb-2">{event.title}</h3>
                    <div className="flex items-center gap-2 text-gray-600 text-sm mb-1">
                      <Calendar className="h-4 w-4" />
                      <span>{event.date.toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600 text-sm">
                      <MapPin className="h-4 w-4" />
                      <span>{event.location}</span>
                    </div>
                  </div>
                  
                  <div className="mt-auto pt-4 border-t border-gray-100">
                    <Link 
                      to="/water-unity/events" 
                      className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm font-medium"
                    >
                      <span>View Details</span>
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </Card>
              </AnimatedSection>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <Link 
              to="/water-unity/events" 
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
            >
              <span>View All Events</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>
      
      {/* How to Participate */}
      <div className="py-24 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <SectionHeader
            title="How to Participate"
            subtitle="Join the global movement to protect and honor sacred waters"
            icon={<Heart className="h-5 w-5" />}
            badgeText="Get Involved"
            badgeColor="blue"
          />
          
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <AnimatedSection delay={0.1}>
                <Card className="h-full">
                  <div className="bg-blue-100 p-4 rounded-lg w-16 h-16 flex items-center justify-center mb-6">
                    <Camera className="h-8 w-8 text-blue-600" />
                  </div>
                  
                  <h3 className="text-xl font-bold mb-4">Document Water Sources</h3>
                  <p className="text-gray-700 mb-6">
                    Help map and document water sources in your area, including their status, cultural significance, and any threats they face. Your contributions help create a global database of sacred waters.
                  </p>
                  
                  <div className="mt-auto pt-4 border-t border-gray-100">
                    <Link 
                      to="/water-unity/submit-source" 
                      className="text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium"
                    >
                      <span>Submit Water Source</span>
                      <ArrowRight className="h-5 w-5" />
                    </Link>
                  </div>
                </Card>
              </AnimatedSection>
              
              <AnimatedSection delay={0.2}>
                <Card className="h-full">
                  <div className="bg-blue-100 p-4 rounded-lg w-16 h-16 flex items-center justify-center mb-6">
                    <Users className="h-8 w-8 text-blue-600" />
                  </div>
                  
                  <h3 className="text-xl font-bold mb-4">Create Your Profile</h3>
                  <p className="text-gray-700 mb-6">
                    Join our community of water protectors by creating your profile. Share your story, connection to water, and the traditions you practice or wish to learn about.
                  </p>
                  
                  <div className="mt-auto pt-4 border-t border-gray-100">
                    <Link 
                      to="/water-unity/join" 
                      className="text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium"
                    >
                      <span>Create Profile</span>
                      <ArrowRight className="h-5 w-5" />
                    </Link>
                  </div>
                </Card>
              </AnimatedSection>
              
              <AnimatedSection delay={0.3}>
                <Card className="h-full">
                  <div className="bg-blue-100 p-4 rounded-lg w-16 h-16 flex items-center justify-center mb-6">
                    <Calendar className="h-8 w-8 text-blue-600" />
                  </div>
                  
                  <h3 className="text-xl font-bold mb-4">Attend or Host Events</h3>
                  <p className="text-gray-700 mb-6">
                    Participate in water ceremonies, cleanups, and educational gatherings in your area, or organize your own event with guidance from our resources and community support.
                  </p>
                  
                  <div className="mt-auto pt-4 border-t border-gray-100">
                    <Link 
                      to="/water-unity/events" 
                      className="text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium"
                    >
                      <span>Explore Events</span>
                      <ArrowRight className="h-5 w-5" />
                    </Link>
                  </div>
                </Card>
              </AnimatedSection>
              
              <AnimatedSection delay={0.4}>
                <Card className="h-full">
                  <div className="bg-blue-100 p-4 rounded-lg w-16 h-16 flex items-center justify-center mb-6">
                    <FileText className="h-8 w-8 text-blue-600" />
                  </div>
                  
                  <h3 className="text-xl font-bold mb-4">Share & Learn Wisdom</h3>
                  <p className="text-gray-700 mb-6">
                    Access our library of water wisdom resources including videos, articles, and guides. With permission from elders, contribute your own knowledge and practices.
                  </p>
                  
                  <div className="mt-auto pt-4 border-t border-gray-100">
                    <Link 
                      to="/water-unity/resources" 
                      className="text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium"
                    >
                      <span>Browse Resources</span>
                      <ArrowRight className="h-5 w-5" />
                    </Link>
                  </div>
                </Card>
              </AnimatedSection>
            </div>
            
            <AnimatedSection delay={0.5}>
              <div className="bg-blue-600 text-white rounded-xl p-8 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 opacity-10">
                  <CloudRain className="h-48 w-48" />
                </div>
                
                <div className="relative z-10">
                  <h3 className="text-2xl font-bold mb-4">Join the Water Unity Network Today</h3>
                  <p className="text-lg text-white/90 mb-6">
                    Become part of a global community dedicated to protecting and honoring sacred waters through traditional knowledge and collaborative action.
                  </p>
                  
                  <Button 
                    variant="outline"
                    size="lg"
                    className="border-white text-white hover:bg-white/10 inline-flex"
                  >
                    <Link to="/water-unity/join" className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Join Our Network
                    </Link>
                  </Button>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>
      
      {/* Featured Resources */}
      <div className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <SectionHeader
            title="Featured Resources"
            subtitle="Explore our collection of water wisdom, teachings, and practical guides"
            icon={<FileText className="h-5 w-5" />}
            badgeText="Wisdom Library"
            badgeColor="blue"
          />
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {featuredResources.map((resource, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <Card className="h-full flex flex-col">
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <Badge 
                        variant={
                          resource.type === 'video' ? 'red' : 
                          resource.type === 'audio' ? 'purple' : 
                          resource.type === 'text' ? 'blue' : 
                          'gray'
                        } 
                        className="capitalize"
                      >
                        {resource.type}
                      </Badge>
                      {resource.elderTeaching && (
                        <Badge variant="amber" icon={<Users className="h-3 w-3" />}>
                          Elder Teaching
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-bold text-xl mb-3">{resource.title}</h3>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {resource.categories.map((category, i) => (
                        <span key={i} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                          {category}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mt-auto pt-4 border-t border-gray-100">
                    <Link 
                      to="/water-unity/resources" 
                      className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm font-medium"
                    >
                      <span>View Resource</span>
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </Card>
              </AnimatedSection>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <Link 
              to="/water-unity/resources" 
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
            >
              <span>Browse All Resources</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>
      
      {/* Call to Action */}
      <div className="py-24 bg-blue-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
            <path fill="white" d="M38.9,-67.2C52.6,-58.9,67.6,-52.8,75.6,-41.2C83.7,-29.7,84.9,-12.8,83.6,3.1C82.3,19.1,78.5,34.1,69.9,45.7C61.2,57.4,47.6,65.7,33.5,72C19.3,78.2,4.6,82.5,-10.7,81C-25.9,79.5,-41.7,72.4,-54.6,62C-67.5,51.6,-77.3,38.1,-81.2,23.3C-85.1,8.4,-83.1,-7.7,-77.7,-22.3C-72.3,-36.8,-63.3,-49.7,-51.1,-59C-38.9,-68.3,-23.5,-73.9,-8.9,-71.4C5.8,-68.9,25.2,-75.5,38.9,-67.2Z" transform="translate(100 100)" />
          </svg>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto text-center">
              <Badge 
                variant="white" 
                className="mb-6 bg-white/20 text-white"
                icon={<Droplet className="h-4 w-4" />}
              >
                Join The Movement
              </Badge>
              
              <h2 className="text-4xl font-bold mb-6">
                Water Unites Us All
              </h2>
              
              <p className="text-xl mb-8 text-white/90">
                Every person has a role in protecting and honoring water. Join our global network of water protectors, elders, and allies working to safeguard the world's sacred waters.
              </p>
              
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="bg-white text-blue-700 border-white hover:bg-blue-50"
                >
                  <Link to="/water-unity/join" className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    <span>Join Our Network</span>
                  </Link>
                </Button>
                
                <Button 
                  variant="outline"  
                  size="lg"
                  className="bg-transparent text-white border-white hover:bg-white/10"
                >
                  <Link to="/water-unity/resources" className="flex items-center gap-2">
                    <PanelRight className="h-5 w-5" />
                    <span>Access Dashboard</span>
                  </Link>
                </Button>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </div>
  );
};

export default WaterUnityPage;